import type { IndicatorProps, OptionProps, PlaceholderProps, Props as SelectProps, ValueType } from "react-select";
import Select, { components } from "react-select";
import { animated, useSpring } from "react-spring";
import { useTheme } from "@material-ui/core";

import { ExpandLess as ExpandLessIcon } from "@eroad/icons/material";

import type { CoretexTheme } from "../../theme";
import Checkbox from "../Checkbox";
import type { DropdownOption } from "./DropdownOption";
import { checkboxDropdownStyle } from "./dropdownStyle";

const Placeholder = ({ ...props }: PlaceholderProps<DropdownOption, true>) => {
  const { getValue, selectProps } = props;
  const selected = getValue() as DropdownOption[];
  const multiText =
    selected.length === 1 ? selected[0].label : `${selected.length} ${selectProps.selectedText ?? "selected"}`;
  const labelText = selected.length > 0 ? multiText : "-";
  return (
    <components.Placeholder {...props}>
      <span>{labelText}</span>
    </components.Placeholder>
  );
};

const CheckOption = (props: OptionProps<DropdownOption, true>) => {
  const { label, isMulti, getValue } = props;
  const selected = getValue();
  const multi = (
    <components.Option {...props}>
      <Checkbox
        checked={(!!selected && selected?.findIndex((x) => x.label === label) >= 0) ?? false}
        onChange={() => null}
        name={`check-${label}`}
        color="primary"
        disableFocusRipple
        disableRipple
        style={{ padding: "2px 0 0 0", marginRight: "8px" }}
      />
      <span data-testid={`dropdown-option-${label}`}>{label}</span>
    </components.Option>
  );
  const single = (
    <components.Option {...props}>
      <span data-testid={`dropdown-option-${label}`}>{label}</span>
    </components.Option>
  );
  return isMulti ? multi : single;
};

const DropdownIndicator = ({ selectProps }: IndicatorProps<DropdownOption, true>) => {
  const AnimatedIcon = animated(ExpandLessIcon);

  const animationPaperConfig = {
    mass: 2,
    friction: 20,
    tension: 120,
    clamp: true,
  };

  const theme = useTheme<CoretexTheme>();

  const rotateAnimation = useSpring({
    config: animationPaperConfig,
    transform: selectProps.menuIsOpen ? "rotate(0deg)" : "rotate(-180deg)",
  });

  return (
    <AnimatedIcon
      style={{
        ...rotateAnimation,
        color: selectProps.menuIsOpen ? theme.palette.colour.primaryBlue : theme.palette.mono.primaryDark,
      }}
    />
  );
};

const CheckboxDropdown = (props: SelectProps<DropdownOption, boolean>): JSX.Element => {
  const {
    options,
    isMulti,
    hideSelectedOptions,
    value,
    defaultValue,
    onChange,
    selectedText,
  }: SelectProps<DropdownOption, boolean> = props;
  return (
    <Select
      options={options}
      isSearchable
      isMulti={isMulti ? true : undefined}
      closeMenuOnSelect={!isMulti}
      selectedText={selectedText}
      components={{
        DropdownIndicator,
        Option: CheckOption,
        IndicatorSeparator: () => null,
        ClearIndicator: () => null,
        Placeholder,
      }}
      styles={checkboxDropdownStyle}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      hideSelectedOptions={hideSelectedOptions}
      value={value as ValueType<DropdownOption, true>}
      defaultValue={defaultValue}
      onChange={onChange}
      controlShouldRenderValue={false}
    />
  );
};

export default CheckboxDropdown;
